// Single source of truth for "may this employee see / do visitor things" — RON-5466.
//
// Combines the two independent gates so no screen has to know about either storage detail:
//   1. Company entitlement  → tblcompany_property (visitor_portal / contractor_portal / max_sites)
//   2. Host invite rights   → tblvisitor_host_permission (RON-5463)
//
// Used by the Sidebar (to show/hide the Visitors item) and by VisitorUI (to decide whether the
// route renders the module or the unavailable state).
//
// COVERAGE CAVEAT FOR WIRING TIME (flagged, not yet a problem while mocked):
// the agreed carrier for the entitlement flags is `HomeScreenBookings_api`, which today is
// only called by the Home screen (`/booking`). A user who lands directly on `/visitors`, or a
// Teams user who lands on `/calendar`, would therefore have no flags yet and — because the
// default is OFF — would not see the module. `HasEmergencyRoles` avoids this by defaulting to
// visible AND being synced from `Calendar_api` / `MapGrid_api` / `MapCanvas_api` too. When the
// flags are wired, they should ride on a payload every entry path receives (`Profile_api` is
// the natural candidate) or be added to the same broad endpoint set.

import { useCallback, useEffect, useState } from "react";

import {
  VISITOR_ENTITLEMENT_EVENT,
  getVisitorEntitlement,
} from "../../utils/visitorEntitlement.js";
import {
  MOCK_ENTITLEMENT,
  USE_VISITOR_MOCKS,
} from "./visitorMockData.js";
import { fetchHostPermissions } from "./VisitorAPI.jsx";

const NO_PERMISSIONS = {
  canVisitor: false,
  canContractor: false,
  canParking: false,
  canDesk: false,
  canReception: false,
};

const readEntitlement = () =>
  USE_VISITOR_MOCKS ? MOCK_ENTITLEMENT : getVisitorEntitlement();

// The STARTING value, before the server has answered. Nothing, deliberately, on both paths: the
// module must not flash into the sidebar and then vanish when the real rights arrive, and a link
// to a screen the server refuses is worse than a link that appears a moment late.
//
// Host rights used to have no real source at all, so an unmocked build granted nothing for ever
// and flipping `USE_VISITOR_MOCKS` hid the whole module. `GET /visitor/me/host-permissions`
// (RON-6002 §4.1) is that source, and the effect below reads it.
const readPermissions = () => NO_PERMISSIONS;

export function useVisitorAccess() {
  const [entitlement, setEntitlement] = useState(readEntitlement);
  const [permissions, setPermissions] = useState(readPermissions);

  const refresh = useCallback(() => {
    setEntitlement(readEntitlement());
  }, []);

  // §4.1 — the five rights, from the server. Mocked or live, it is the same call: `fetchHostPermissions`
  // reads the mock in wire format and maps it exactly as it maps the real answer.
  //
  // A failure leaves the rights at NO_PERMISSIONS, which hides the module. That is the safe
  // direction: the alternative is offering a host actions the backend will refuse.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { ok, permissions: granted } = await fetchHostPermissions();

      if (!cancelled && ok) setPermissions(granted);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Same event-bus approach the Sidebar already uses for `hasEmergencyRolesChanged`, so a
    // mid-session entitlement change reaches every mounted consumer.
    window.addEventListener(VISITOR_ENTITLEMENT_EVENT, refresh);

    return () => {
      window.removeEventListener(VISITOR_ENTITLEMENT_EVENT, refresh);
    };
  }, [refresh]);

  const { visitorPortal, contractorPortal, maxSites } = entitlement;

  // The module needs BOTH the company entitlement and a personal invite right. An entitled
  // company whose employee has no rights should not see the nav item at all.
  // THE SERVER IS THE GATE. These flags are `GET /visitor/me/host-permissions` (§4.1), which the
  // contract says to "call before rendering anything — otherwise you show an 'Invite' button that
  // 403s". That is exactly what this is for.
  //
  // IT USED TO BE `visitorPortal && (…)`, and that is what hid both menus on 1-October-2026 when
  // `USE_VISITOR_MOCKS` went false. The entitlement flags live in localStorage and NOTHING WRITES
  // THEM — `setVisitorEntitlement` has no callers outside tests — so `visitorPortal` was
  // permanently false and the `&&` hid the module from everybody, including the hosts the server
  // would have let straight through.
  //
  // Dropping it is not a hole: the client check was never the security boundary. §3 names two
  // gates, the role capability and the host grant, and the server applies both — every endpoint
  // 403s without them. This check only ever existed to avoid offering a menu that leads to a 403,
  // and a check with no data cannot do that job.
  //
  // CONFIRMED BY LARAVEL, 1-October-2026 — and it is stronger than the contract implied. Every
  // `/visitor/me/*` route carries `perm.entitled:vcm`, which checks TWO company flags, both
  // required:
  //
  //   tblcompany_property.visitor_portal          a superadmin sold it
  //   tblcompany_property.visitor_portal_enabled  the tenant switched it on
  //
  // Two rather than one, deliberately: one flag would conflate "never bought it" with "bought it
  // and paused it", so a superadmin could not withdraw entitlement without stomping the tenant's
  // setting, and a tenant could not pause without appearing unsold.
  //
  // "They do not need to re-implement it." So the client check is not merely sourceless — it is
  // redundant, and re-adding it would be a second copy of a rule that already has an owner.
  //
  // RECEPTION IS COVERED BY THE SAME GATE, transitively and by design: `canReception` comes from
  // this one call, and this call is `/visitor/me/host-permissions`. An unentitled company gets a
  // 403 here, every capability stays false, and the reception menu never draws — even though the
  // reception routes themselves live under `/visitor/reception/*`.
  const canSeeVisitors = permissions.canVisitor || permissions.canContractor;

  return {
    visitorPortal,
    contractorPortal,
    maxSites,
    canSeeVisitors,
    canInviteVisitor: permissions.canVisitor,
    // The contractor tick is its own capability on the Hosts screen, and the server refuses a
    // contractor invite without it — "403: host grant exists, Contractors not ticked". So this
    // flag alone is the right question; the add-on sits upstream of an admin ever ticking it.
    canInviteContractor: permissions.canContractor,
    canAssignDesk: permissions.canDesk,
    canAssignParking: permissions.canParking,
    // The reception dashboard (RON-5469). Gated on EITHER portal rather than on `visitorPortal`
    // alone: a front desk signs in whoever turns up, and a company that bought only the
    // contractor add-on still has a desk with contractors arriving at it.
    //
    // Coerced to a real boolean, because the Sidebar's nav filter hides an item only when
    // `visible` is exactly `false` — an undefined capability would show a link to a screen the
    // server refuses.
    canReception: Boolean(permissions.canReception),
  };
}

export default useVisitorAccess;
